import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonGrid, IonRow, IonCol
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';

import { environment } from '@app/environment';
import { MarvColorPrimary } from '@marv/types';
import { MarvValidatorsService } from '@marv/services';
import { 
    MarvButtonComponent, MarvSelectItem, MarvSelectExternalSource, MarvSelectComponent, MarvToggleComponent
} from '@marv/components';

export class PermanentPlanningFiltersValues {  

    sectorsIds: Array<number> = [];
    retailersIds: Array<number> = [];
    rankingsIds: Array<number> = [];
    profilsIds: Array<number> = [];
    displayAllAreaSectors: boolean = false;
    nbFiltersFilled: number = 0;
    
    static FromForm(form: FormGroup): PermanentPlanningFiltersValues{
        let filters = new PermanentPlanningFiltersValues();
        
        if(form.value?.sectorsIds && form.value.sectorsIds.length > 0){
            filters.sectorsIds = form.value.sectorsIds;
            filters.nbFiltersFilled++;
        }
        
        if(form.value?.retailersIds && form.value.retailersIds.length > 0){
            filters.retailersIds = form.value.retailersIds;
            filters.nbFiltersFilled++;
        }
        
        if(form.value?.rankingsIds && form.value.rankingsIds.length > 0){
            filters.rankingsIds = form.value.rankingsIds;
            filters.nbFiltersFilled++;
        }
        
        if(form.value?.profilsIds && form.value.profilsIds.length > 0){
            filters.profilsIds = form.value.profilsIds;
            filters.nbFiltersFilled++;
        }
        
        if(form.value?.displayAllAreaSectors === true){
            filters.displayAllAreaSectors = true;
            filters.nbFiltersFilled++;
        }
        
        return filters;
    }
}

@Component({
    selector: 'app-permanent-planning-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, TranslatePipe, ReactiveFormsModule, 
        forwardRef(() => MarvButtonComponent), forwardRef(() => MarvSelectComponent), forwardRef(() => MarvToggleComponent)
    ]
})
export class PermanentPlanningFiltersComponent  implements OnInit {

    @Input({alias : 'operationId', required : true}) operationId: number;
    @Input({alias : 'permanentPlanningFiltersValues', required : false}) permanentPlanningFiltersValues: PermanentPlanningFiltersValues = new PermanentPlanningFiltersValues();
    @Input({alias : 'allowedForAreaScheduling', required : false}) allowedForAreaScheduling: boolean = false;
    @Output() onChangePermanentPlanningFilters = new EventEmitter<PermanentPlanningFiltersValues>();
    
    sectorsExternalSource: MarvSelectExternalSource = new MarvSelectExternalSource();
    retailersExternalSource: MarvSelectExternalSource = new MarvSelectExternalSource();
    rankingsExternalSource: MarvSelectExternalSource = new MarvSelectExternalSource();
    profilsExternalSource: MarvSelectExternalSource = new MarvSelectExternalSource();
    
    filtersPermanentPlanningForm: FormGroup = new FormGroup({
        sectorsIds: new FormControl([]),
        retailersIds: new FormControl([]),
        rankingsIds: new FormControl([]),
        profilsIds: new FormControl([]),
        displayAllAreaSectors: new FormControl(false)
    });
    
    constructor() { }

    ngOnInit() {}

    async ngOnChanges(changes: SimpleChanges) {
        if(changes['permanentPlanningFiltersValues'] && changes['permanentPlanningFiltersValues'].currentValue){
            let values = {
                sectorsIds: (this.permanentPlanningFiltersValues.sectorsIds?.length > 0 ? this.permanentPlanningFiltersValues.sectorsIds : []),
                retailersIds: (this.permanentPlanningFiltersValues.retailersIds?.length > 0 ? this.permanentPlanningFiltersValues.retailersIds : []),
                rankingsIds: (this.permanentPlanningFiltersValues.rankingsIds?.length > 0 ? this.permanentPlanningFiltersValues.rankingsIds : []),
                profilsIds: (this.permanentPlanningFiltersValues.profilsIds?.length > 0 ? this.permanentPlanningFiltersValues.profilsIds : []),
                displayAllAreaSectors: (this.allowedForAreaScheduling && this.permanentPlanningFiltersValues.displayAllAreaSectors === true),
            };
            this.filtersPermanentPlanningForm.setValue(values);
        }
        
        if(changes['operationId']){
            
            if(MarvValidatorsService.isInteger(this.operationId)){ 
            
                this.sectorsExternalSource = {
                    url: 'sales-rep-planning/sectors',
                    params : {
                        ctxId: this.operationId
                    },
                    labelField: 'title',
                    valueField: 'value'
                };
            
                this.retailersExternalSource = {
                    url: 'sales-rep-planning/retailers',
                    params : {
                        ctxId: this.operationId
                    },
                    labelField: 'title',
                    valueField: 'value',
                    transformItem: (json: any): MarvSelectItem => {                    
                        return {
                            label: json['title'],
                            value: json['value'],
                            color: MarvColorPrimary,
                            picture: environment.URL_LOGO_RETAILER.replace('#retailer_name#', json['title'])
                        };
                    }
                };
                
                this.rankingsExternalSource = {
                    url: 'sales-rep-planning/store-ranking',
                    params : {
                        ctxId: this.operationId
                    },
                    labelField: 'title',
                    valueField: 'value'
                };
                
                this.profilsExternalSource = {
                    url: 'sales-rep-planning/store-profiles',
                    params : {
                        ctxId: this.operationId
                    },
                    labelField: 'title',
                    valueField: 'value'
                };
                        
            }
            
        }
    }
    
    search() {        
        if(this.filtersPermanentPlanningForm.valid){            
            this.onChangePermanentPlanningFilters.emit(PermanentPlanningFiltersValues.FromForm(this.filtersPermanentPlanningForm));
        }
        else{
            for (var controlName in this.filtersPermanentPlanningForm.controls) {
                if(!this.filtersPermanentPlanningForm.controls[controlName].valid) {
                    this.filtersPermanentPlanningForm.controls[controlName].markAsTouched();
                }
            }
        }
    }
    
    displayAll() {
        this.filtersPermanentPlanningForm.reset();
        let filters = new PermanentPlanningFiltersValues();
        this.onChangePermanentPlanningFilters.emit(filters);
    }
}
